import { PropsWithChildren, AnchorHTMLAttributes, ComponentProps } from 'react';

import { Link as ReactRouterLink } from 'react-router-dom';

import { isString } from '@@/services/typeGuards';
import { isAbsolute, matchesCurrentOrigin } from '@@/services/url';

type TAnchorProps = { to: string } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>;

type TLinkProps = ComponentProps<typeof ReactRouterLink>;

export type TLink = PropsWithChildren<{
  className?: string;
  'data-testid'?: string;
  href?: never;
  location?: Pick<Location, 'origin'>;
}> &
  (TAnchorProps | TLinkProps);

export default function Link({
  className,
  children,
  'data-testid': dataTestId,
  location = window.location,
  ...props
}: TLink) {
  // Small perf win: if we receive a full URL that happens to be the domain
  // we're already on, we can avoid a full page reload and let react-router
  // handle it by passing along the slug to react-router.
  const to =
    isString(props.to) && matchesCurrentOrigin(props.to, location)
      ? props.to.replace(location.origin, '')
      : props.to;

  return isString(to) && isAbsolute(to) ? (
    <a
      href={to}
      rel="noreferrer"
      className={className}
      data-testid={dataTestId}
      data-test-link-type="html-anchor"
      {...props}
    >
      {children}
    </a>
  ) : (
    <ReactRouterLink
      {...props}
      className={className}
      data-testid={dataTestId}
      data-test-link-type="react-router"
      to={to}
    >
      {children}
    </ReactRouterLink>
  );
}
